<template>
  <div class="content">
    <div class="container-fluid">

      <div class="pt-4">
        <v-card :loading="loading">
          <v-card-title>Receipts
            <span v-if="selected.length" class="selected-count">({{ selected.length }} receipts selected <a
              @click="clearSelection()">clear</a>)</span>
          </v-card-title>
          <v-card-text>
            <div class="pb-4 batch-actions">
              <div class="select-all-btn">
                <v-btn @click="selectAll()">
                  <v-icon>mdi-checkbox-multiple-outline</v-icon>
                  {{ selected && selected.length === receipts && receipts.length ? 'Deselect All' : 'Select All' }}
                </v-btn>
              </div>
              <v-btn :disabled="selected.length===0" @click="downloadSelected()">
                <v-icon>mdi-microsoft-excel</v-icon>
                Download Selection
              </v-btn>
              <v-btn :disabled="selected.length===0" @click="setApproveSelected(true)">
                <v-icon>mdi-text-box-check-outline</v-icon>
                Approve Selection
              </v-btn>
              <v-btn :disabled="selected.length===0" @click="setApproveSelected(false)">
                <v-icon>mdi-text-box-minus-outline</v-icon>
                Disapprove Selection
              </v-btn>
            </div>

            <v-card>
              <div class="filter">
                <v-text-field
                  v-model="nameFilter"
                  label="Name"
                  type="text"
                  @keyup.enter="search">
                </v-text-field>

                <v-select
                  v-model="statusFilter"
                  :items="statuses"
                  label="Show"
                ></v-select>
                <v-text-field
                  v-model="fromCreatedDateFilter"
                  :disabled="statusFilter==='Deleted' || statusFilter==='Approved' || !!fromReceiptDateFilter"
                  label="From Upload Date"
                  type="date">
                </v-text-field>
                <v-text-field
                  v-model="fromReceiptDateFilter"
                  :disabled="statusFilter==='Deleted' || statusFilter==='Approved' || !!fromCreatedDateFilter"
                  label="From Receipt Date"
                  type="date">
                </v-text-field>
                <v-btn type="reset" @click="clearFilter()">
                  Reset
                </v-btn>
                <v-btn @click="search()">
                  <v-icon>mdi-magnify</v-icon>
                  Search
                </v-btn>
              </div>
            </v-card>

            <v-data-table
              v-if="receipts"
              v-model="selected"
              :footer-props="{'items-per-page-options': [10, 25, 50, 100,-1]}"
              :headers="headers"
              :item-class="receiptItemClass"
              :items="receipts"
              :items-per-page="25"
              :sort-by="['user','date']"
              :sort-desc="true"
              class="elevation-1"
              item-key="id"
              multi-sort show-select>

              <template v-slot:item.Status="{ item }">
                <div>
                  <v-tooltip v-if="!item.deleted && item.approved" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="green" v-bind="attrs" @click="clearApproved(item)"
                              v-on="on">
                        mdi-text-box-check
                      </v-icon>
                    </template>
                    <div>Payment Approved</div>
                  </v-tooltip>

                  <v-tooltip v-if="!item.deleted && !item.approved" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="orange" v-bind="attrs" @click="markApproved(item)"
                              v-on="on">
                        mdi-text-box-plus
                      </v-icon>
                    </template>
                    <div>New (Pending Approval)</div>
                  </v-tooltip>

                  <v-tooltip v-if="!!item.deleted" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="red" v-bind="attrs" v-on="on">
                        mdi-text-box-remove
                      </v-icon>
                    </template>
                    <div>Deleted</div>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:item.created="{ item }">
                <div class="text-cell">{{ formatDate(item.created) }}</div>
              </template>
              <template v-slot:item.approved="{ item }">
                <div class="text-cell">{{ formatDate(item.approved) }}</div>
              </template>
              <template v-slot:item.date="{ item }">
                <div class="text-cell">{{ formatDate(item.date) }}</div>
              </template>
              <template v-slot:item.user="{ item }">
                <div class="text-cell">{{ formatUser(item.user) }}</div>
              </template>
              <template v-slot:item.amount="{ item }">
                <div class="text-cell">₪ {{ item.amount }}</div>
              </template>

              <template v-slot:item.kind="{ item }">
                <div class="text-cell">
                  <v-tooltip v-if="item.kind==='sport'" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">mdi-weight-lifter</v-icon>
                    </template>
                    <div>Sport Activities</div>
                  </v-tooltip>
                  <v-tooltip v-if="item.kind==='park'" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">mdi-parking</v-icon>
                    </template>
                    <div>Parking</div>
                  </v-tooltip>
                  <v-tooltip v-if="!item.kind" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">mdi-cash-multiple</v-icon>
                    </template>
                    <div>Other</div>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:item.id="{ item }">
                <div class="receipt-actions">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon :disabled="!!item.deleted || !item.photoPath" v-bind="attrs" @click="openReceipt(item)"
                              v-on="on">
                        mdi-download-box
                      </v-icon>
                    </template>
                    <div>Download</div>
                  </v-tooltip>

                  <v-tooltip v-if="!item.deleted" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon :disabled="!!item.approved" class="delete-icon" v-bind="attrs"
                              @click="deleteReceipt(item)"
                              v-on="on">
                        mdi-delete
                      </v-icon>
                    </template>
                    <div>Delete</div>
                  </v-tooltip>
                  <v-tooltip v-if="!!item.deleted && !item.approved" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon :disabled="!!item.approved" class="undelete-icon" v-bind="attrs"
                              @click="undeleteReceipt(item)" v-on="on">
                        mdi-delete-off
                      </v-icon>
                    </template>
                    <div>Restore</div>
                  </v-tooltip>
                </div>
              </template>

            </v-data-table>
            <div v-if="!receipts && !loading">
              No receipts found.
            </div>
            <div class="pt-4 batch-actions">
              <div class="select-all-btn">
                <v-btn @click="selectAll()">
                  <v-icon>mdi-checkbox-multiple-outline</v-icon>
                  {{ selected && selected.length === receipts && receipts.length ? 'Deselect All' : 'Select All' }}
                </v-btn>
              </div>
              <v-btn :disabled="selected.length===0" @click="downloadSelected()">
                <v-icon>mdi-microsoft-excel</v-icon>
                Download Selection
              </v-btn>
              <v-btn :disabled="selected.length===0" @click="setApproveSelected(true)">
                <v-icon>mdi-text-box-check-outline</v-icon>
                Approve Selection
              </v-btn>
              <v-btn :disabled="selected.length===0" @click="setApproveSelected(false)">
                <v-icon>mdi-text-box-minus-outline</v-icon>
                Disapprove Selection
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import { collection, doc, getDoc, getDocs, getFirestore, query, setDoc, where, writeBatch } from 'firebase/firestore';
import { firebase } from '../services/FirebaseService';
import moment from 'moment';
import { first } from 'rxjs';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import { settings$ } from '../services/settings.util';
import { router } from '../routes/router';
import { downloadReceiptsExcel } from '../services/excel.util';

function toDate(date) {
  return date && moment(date.seconds * 1000).startOf('day').toDate();
}

const mapModelToView = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    date: toDate(data.date),
    amount: data.amount.toFixed(2),
    kind: data.kind,
    user: data.user && data.user.replace(/\@cytoreason\.com$/, ''),
    photoPath: data.photoPath,
    approved: toDate(data.approved),
    created: toDate(data.created),
    Status: data.deleted ? 'Removed' : data.approved ? 'Approved' : 'New',
    deleted: data.deleted,
  };
};

export default {
  data: () => ({
    loading: false,
    saving: false,
    showOnlyNew: true,
    selected: [],
    receipts: null,
    nameFilter: '',
    statusFilter: 'New',
    fromCreatedDateFilter: '',
    fromReceiptDateFilter: '',
    statuses: ['New', 'Approved', 'Deleted', 'All'],
    showPreview: false,
    users: {},
    headers: [
      { text: 'Status', value: 'Status' },
      { text: 'User', value: 'user' },
      { text: 'Uploaded', value: 'created' },
      { text: 'Approved', value: 'approved' },
      { text: 'Date', value: 'date' },
      { text: 'Amount', value: 'amount' },
      { text: 'Kind', value: 'kind' },
      { text: 'Actions', value: 'id' },
    ],
  }),

  mounted() {
    this.db = getFirestore();
    this.storage = getStorage();

    settings$.pipe(first()).subscribe((settings) => {

      if (!settings.admin) {
        router.push('/scriba/');
      }
    });

    // wait for user to load
    firebase.user$.pipe(first()).subscribe(() => {
      this.loadUserNames();
      this.search();
    });
  },

  methods: {
    formatDate(date) {
      return date && moment(date).format('DD/MM/YYYY');
    },

    async loadUserNames() {
      this.users = {};
      const results = await getDocs(query(collection(this.db, 'users')));
      results.forEach(userDoc => {
        const user = userDoc.data();
        const accountName = user.email.replace(/\@cytoreason\.com$/, '');
        this.users[accountName] = user.displayName;
      });

    },
    async updateReceipt(existingReceipt, receiptData) {
      await this.task('loading', async () => {
        const receiptRef = doc(this.db, 'receipts', existingReceipt.id);
        await setDoc(receiptRef, receiptData, { merge: true });
        const receiptDoc = await getDoc(receiptRef);
        Object.assign(existingReceipt, mapModelToView(receiptDoc));
      });
    },

    receiptItemClass(receipt) {
      return 'row-' + receipt.Status;
    },
    async markApproved(receipt) {
      await this.updateReceipt(receipt, { approved: new Date() });
    },
    async clearApproved(receipt) {
      await this.updateReceipt(receipt, { approved: null });
    },

    async downloadSelected() {
      const receipts = this.selected.map(item => ({ ...item, user: this.formatUser(item.user) }));
      await downloadReceiptsExcel(receipts);
    },

    clearSelection() {
      this.selected = [];
    },

    selectAll() {
      if (this.selected.length === this.receipts.length) {
        this.selected = [];
      } else {
        this.selected = this.receipts;
      }
    },

    async setApproveSelected(isApproved) {
      await this.task('loading', async () => {
        const db = this.db;
        const batch = writeBatch(db);
        const approved = isApproved ? new Date() : null;
        this.selected.forEach(receipt => {
          if (!receipt.deleted) {
            const receiptRef = doc(db, 'receipts', receipt.id);
            batch.update(receiptRef, { approved }, { merge: true });
          }
        });
        await batch.commit();
        await this.loadReceipts();
      });
    },

    async openReceipt(receipt) {
      const downloadUrl = await getDownloadURL(ref(this.storage, receipt.photoPath));
      window.open(downloadUrl, '_blank');
    },

    async deleteReceipt(receipt) {
      await this.updateReceipt(receipt, { deleted: new Date() });
    },

    async undeleteReceipt(receipt) {
      await this.updateReceipt(receipt, { deleted: null });
    },

    async clearFilter() {
      this.statusFilter = 'New';
      this.fromCreatedDateFilter = '';
      this.fromReceiptDateFilter = '';
      await this.search();
    },
    async search() {
      await this.task('loading', async () => this.loadReceipts());
    },

    formatUser(user) {
      return user && (this.users[user] || user.replace(/\./, ' ').replace(/\b\w/g, c => c.toUpperCase()));
    },
    createFilter() {
      const filters = [];
      if (this.statusFilter === 'Deleted') {
        filters.push(where('deleted', '!=', null));
      } else {
        filters.push(where('deleted', '==', null));
      }
      if (this.statusFilter === 'New') {
        filters.push(where('approved', '==', null));
      } else if (this.statusFilter === 'Approved') {
        filters.push(where('approved', '!=', null));
      }
      if (this.fromReceiptDateFilter) {
        filters.push(where('date', '>=', moment(this.fromReceiptDateFilter).toDate()));
      }
      if (this.fromCreatedDateFilter) {
        filters.push(where('created', '>=', moment(this.fromCreatedDateFilter).toDate()));
      }
      return filters;
    },
    getUserName(doc) {
      return this.formatUser(doc.data().user.replace(/\@cytoreason\.com$/, ''));
    },
    async loadReceipts() {
      const results = await getDocs(query(collection(this.db, 'receipts'),
        ...this.createFilter(),
      ));

      // filter by name
      const nameFilter = this.nameFilter.toLowerCase();
      const receipts = [];
      results.forEach((doc) => {
        if (!nameFilter || this.getUserName(doc).toLowerCase().includes(nameFilter)) {
          receipts.push(mapModelToView(doc));
        }
      });

      this.receipts = receipts;
    },

    async task(flagName, runnable) {
      this[flagName] = true;
      try {
        await runnable();
      } finally {
        this[flagName] = false;
      }
    },
  },
};
</script>
<style lang="scss">
h4 {
  margin: 0 0 1rem;
}

.kind-name {
  padding-left: 1rem;
}

.batch-actions {
  button {
    margin-left: 0.5rem;
  }
}

.pointer {
  cursor: pointer;
}

.v-data-table {
  .theme--light.v-icon.v-icon.v-icon--disabled {
    color: #dddddd !important;
  }
}

.v-icon.v-icon.v-icon--link.delete-icon {
  color: darkred;
}

.v-icon.v-icon.v-icon--link.undelete-icon {
  color: darkgreen;
}

.row-Approved {
  background-color: #fafffd;
}

.row-Removed {
  background-color: #fff3f3;

  .text-cell {
    text-decoration: line-through;
  }
}

.select-all-btn {
  width: 200px;
  display: inline-block;
}

.filter {
  display: flex;
  padding: 1rem 1rem 0 1rem;

  .v-input {
    margin-right: 1rem;
  }

  button {
    margin-left: 1rem;
    align-self: center;
  }
}

.selected-count {
  font-size: 1rem;
  font-weight: normal;
  padding-left: 1rem;

  a {
    color: blue !important;
    text-decoration: underline !important;
  }
}
</style>
